import React, {
  createContext,
  ReactNode,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from 'react'
import { Spinner } from 'transfix-ui/components/Spinner'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { IDispatcher } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import * as Apis from 'apps/fleet-planner/utils/api'
import { fetchDispatchers } from 'apps/fleet-planner/utils/api/dispatcher'
import { getUserSettingsFromUser } from 'apps/fleet-planner/utils/getUserData'
import {
  getTimeFormats,
  ITimeFormats,
  standardFormats,
} from 'apps/fleet-planner/utils/userDateTimeSettings'

export interface IUserData {
  user?: Types.IUser
  settings?: Types.ISettings
  motorCarriers?: Types.IMotorCarrier[]
  truckAvailabilityShareUuid?: string
}

export interface IUserFlags {
  onboardingHasAddedCarriers: boolean
  marketplaceLoadSyncViewed: boolean
  truckAvailabilityDismissed: boolean
}

const defaultUserData: IUserData = {
  user: undefined,
  settings: undefined,
  motorCarriers: [],
}

const defaultUserFlags: IUserFlags = {
  onboardingHasAddedCarriers: false,
  marketplaceLoadSyncViewed: false,
  truckAvailabilityDismissed: false,
}

interface IUseCurrentUser {
  hasLoadedUser: boolean
  isDispatcherListLoading: boolean
  userData?: IUserData
  userFlags: IUserFlags
  errors?: string
  fetchUserData: () => Promise<void>
  clearUserData: () => Promise<void>
  getDispatchers: () => Promise<void>
  getUser: () => Types.IUser | undefined
  dispatchers: IDispatcher[]
}

// Custom Hook to encapsulate the current user data & methods
const useCurrentUser = (): IUseCurrentUser => {
  const [userData, setUserData] = useState<IUserData>(defaultUserData)
  const { user } = userData || {}
  const [hasLoadedUser, setHasLoadedUser] = useState<boolean>(false)
  const [isDispatcherListLoading, setIsDispatcherListLoading] = useState<boolean>(false)
  const [dispatchers, setDispatchers] = useState<IDispatcher[]>([])
  const [errors, setErrors] = useState<string | undefined>()
  const { isFrontChatBooted, update } = useChatWidgetContext()

  // identifies the user to the mounted chat service when user data is returned
  useEffect(() => {
    if (user?.fullName && isFrontChatBooted) {
      update({
        name: user?.fullName,
        email: user?.email,
        userId: user?.id,
        userHash: user?.frontUserHash,
      })
    }
  }, [user, isFrontChatBooted, update])

  const userFlags = useMemo<IUserFlags>(
    () => ({
      onboardingHasAddedCarriers: !!userData?.motorCarriers?.length,
      marketplaceLoadSyncViewed: !!userData?.user?.onboarding?.marketplaceLoadSyncViewed,
      truckAvailabilityDismissed: !!userData?.user?.onboarding?.truckAvailabilityDismissed,
    }),
    [userData]
  )

  /**
   * Fetch dispatchers data
   */
  const getDispatchers = useCallback(async (): Promise<void> => {
    setErrors(undefined)
    setIsDispatcherListLoading(true)
    try {
      const { dispatchers: response } = await fetchDispatchers()
      setDispatchers(response)
    } catch (e) {
      setErrors(e)
    }
    setIsDispatcherListLoading(false)
  }, [])

  /**
   * Fetch the user entity data from /current_user
   */
  const fetchUserData = useCallback(async (): Promise<void> => {
    setErrors(undefined)
    try {
      const res = await Apis.fetchCurrentUser()
      setUserData(res)
      setHasLoadedUser(true)
    } catch (e) {
      setErrors(e)
    }
  }, [setUserData, setHasLoadedUser])

  /**
   * Reset the local user data object
   */
  const clearUserData = useCallback(async (): Promise<void> => {
    setUserData(defaultUserData)
    setHasLoadedUser(false)
    setErrors(undefined)
  }, [setUserData, setHasLoadedUser])

  /**
   * Helper method to return user info
   */
  const getUser = useCallback((): Types.IUser | undefined => {
    const { user } = userData || {}
    return user
  }, [userData])

  return {
    userData,
    userFlags,
    errors,
    hasLoadedUser,
    fetchUserData,
    clearUserData,
    isDispatcherListLoading,
    dispatchers,
    getDispatchers,
    getUser,
  }
}

export interface IUserState extends IUseCurrentUser {
  isLoading: boolean
  errors?: string
  updateUserSettings: (payload: Types.IUpdateUserSettingsPayload) => Promise<void>
  updateUserOnboarding: (payload: Types.IUpdateUserOnboardingRequest) => Promise<void>
  timeFormat: Types.SETTINGS_TIME_FORMAT
  timeFormats: ITimeFormats
  updateUserInfo: (payload: Types.IUpdateUserInfoPayload) => Promise<void>
  joinMotorCarriers: (payload: Types.IJoinMotorCarriersPayload) => Promise<boolean>
}

/**
 * Init the context
 */
export const userDefaultState: IUserState = {
  isLoading: false,
  hasLoadedUser: false,
  userData: defaultUserData,
  userFlags: defaultUserFlags,
  errors: undefined,
  timeFormats: standardFormats,
} as IUserState

export const UserContext = createContext<IUserState>(userDefaultState)

/**
 * Provide default Provider state component to wrap around app
 */
export function UserProvider({ children }: { children: ReactNode }): JSX.Element {
  const {
    clearUserData,
    dispatchers,
    errors: userErrors,
    fetchUserData,
    getDispatchers,
    getUser,
    hasLoadedUser,
    isDispatcherListLoading,
    userData,
    userFlags,
  } = useCurrentUser()
  const [errors, setErrors] = useState<string | undefined>(userErrors)
  const [isLoading, setLoading] = useState<boolean>(false)

  // Update the users name
  const updateUserInfo = useCallback(
    async (payload: Types.IUpdateUserInfoPayload): Promise<void> => {
      setLoading(true)
      try {
        await Apis.updateUserInfo(payload)
        setLoading(false)
      } catch (e) {
        setErrors(e)
        setLoading(false)
      }

      await fetchUserData()
    },
    [fetchUserData]
  )

  // Update the users time format and fleet view day settings
  const updateUserSettings = useCallback(
    async (payload: Types.IUpdateUserSettingsPayload): Promise<void> => {
      setLoading(true)
      try {
        await Apis.updateUserSettings(payload)
        setLoading(false)
      } catch (e) {
        setErrors(e)
        setLoading(false)
      }

      await fetchUserData()
    },
    [fetchUserData]
  )

  // Update the user with the provided Entity object
  const updateUserOnboarding = useCallback(
    async (payload: Types.IUpdateUserOnboardingRequest): Promise<void> => {
      setLoading(true)
      try {
        setLoading(false)
        await Apis.updateUserOnboarding(payload)
      } catch (e) {
        setLoading(false)
        setErrors(e)
      }

      await fetchUserData()
    },
    [fetchUserData]
  )

  const joinMotorCarriers = useCallback(
    async (payload: Types.IJoinMotorCarriersPayload): Promise<boolean> => {
      let success = false
      setLoading(true)
      try {
        setLoading(false)
        await Apis.joinMotorCarriers(payload)
        success = true
      } catch (e) {
        setLoading(false)
        setErrors(e)
      }

      await fetchUserData()
      return success
    },
    [fetchUserData]
  )

  /**
   * Load up the user data in local state
   * @note This should only run once, when the app loads
   */
  useEffect(() => {
    if (!hasLoadedUser) {
      fetchUserData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { timeFormat } = getUserSettingsFromUser(userData)
  const timeFormats = getTimeFormats(timeFormat)

  const userState = {
    hasLoadedUser,
    userData,
    userFlags,
    errors,
    fetchUserData,
    clearUserData,
    updateUserOnboarding,
    isDispatcherListLoading,
    dispatchers,
    getDispatchers,
    updateUserSettings,
    timeFormat,
    timeFormats,
    updateUserInfo,
    isLoading,
    getUser,
    joinMotorCarriers,
  }

  return (
    <UserContext.Provider value={userState}>
      {userState.hasLoadedUser ? children : <Spinner />}
    </UserContext.Provider>
  )
}

// Helper method for accessing this context
export const useUserContext = () => useContext(UserContext)
